import type { JSX, RefObject } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import {
  calculateViewerLayout,
  rotateScreen,
  type ScreenRotation,
  type ViewerDisplayMode,
} from '../media/viewerLayout';

export function useMediaStream(
  videoRef: RefObject<HTMLVideoElement>,
  stream: MediaStream | null,
  onAutoplayBlocked?: () => void,
) {
  useEffect(() => {
    const video = videoRef.current;
    if (video === null) return;
    video.srcObject = stream;
    if (stream !== null) {
      void video.play().catch(() => onAutoplayBlocked?.());
    }
    return () => {
      if (video.srcObject === stream) video.srcObject = null;
    };
  }, [onAutoplayBlocked, stream, videoRef]);
}

export interface HostPreviewProps {
  readonly stream: MediaStream | null;
}

export function HostPreview({ stream }: HostPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  useMediaStream(videoRef, stream);
  return (
    <div class="media-stage media-stage--host">
      <video ref={videoRef} autoPlay muted playsInline aria-label="Shared screen preview" />
      <div class="preview-label">
        <span aria-hidden="true" />
        Live preview
      </div>
    </div>
  );
}

export interface ViewerStageProps {
  readonly stream: MediaStream | null;
  readonly fullscreenSupported: boolean;
}

export function ViewerStage({ stream, fullscreenSupported }: ViewerStageProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinchStart = useRef<{ distance: number; zoom: number } | null>(null);
  const [mode, setMode] = useState<ViewerDisplayMode>('fit');
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState<ScreenRotation>(0);
  const [volume, setVolume] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const handleAutoplayBlocked = useCallback(() => setPlaybackBlocked(true), []);

  useMediaStream(videoRef, stream, handleAutoplayBlocked);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === stageRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (video === null) return;
    video.volume = volume;
    video.muted = volume === 0;
  }, [volume]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const updateSize = () =>
      setViewportSize({ width: canvas.clientWidth, height: canvas.clientHeight });
    updateSize();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }
    const observer = new ResizeObserver(updateSize);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  const hasVideo = Boolean(stream?.getVideoTracks().some((track) => track.readyState === 'live'));
  const setDisplayMode = (next: ViewerDisplayMode) => {
    setMode(next);
    setZoom(1);
  };
  const setSafeZoom = (value: number) => setZoom(Math.min(4, Math.max(0.5, value)));

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement === stageRef.current) await document.exitFullscreen();
      else await stageRef.current?.requestFullscreen();
    } catch {
      // The browser can reject fullscreen when policy or transient activation prevents it.
    }
  };

  const handlePointerDown: JSX.PointerEventHandler<HTMLDivElement> = (event) => {
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    event.currentTarget.setPointerCapture?.(event.pointerId);
    if (pointers.current.size === 2) {
      pinchStart.current = { distance: pointerDistance(pointers.current), zoom };
    }
  };
  const handlePointerMove: JSX.PointerEventHandler<HTMLDivElement> = (event) => {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 2 && pinchStart.current !== null) {
      const distance = pointerDistance(pointers.current);
      setSafeZoom(pinchStart.current.zoom * (distance / pinchStart.current.distance));
    }
  };
  const handlePointerEnd: JSX.PointerEventHandler<HTMLDivElement> = (event) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
  };

  const layout = calculateViewerLayout({
    mode,
    rotation,
    zoom,
    naturalWidth: naturalSize.width,
    naturalHeight: naturalSize.height,
    viewportWidth: viewportSize.width,
    viewportHeight: viewportSize.height,
  });
  const planeStyle: JSX.CSSProperties | undefined = layout
    ? {
        width: `max(100%, ${layout.rotatedWidth}px)`,
        height: `max(100%, ${layout.rotatedHeight}px)`,
      }
    : undefined;
  const videoStyle: JSX.CSSProperties = layout
    ? {
        width: `${layout.videoWidth}px`,
        height: `${layout.videoHeight}px`,
        transform: `rotate(${rotation}deg)`,
      }
    : { transform: `rotate(${rotation}deg)` };

  return (
    <div class="viewer-media">
      <div
        class={`media-stage media-stage--viewer media-stage--${mode}`}
        ref={stageRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onLostPointerCapture={handlePointerEnd}
      >
        <div class="media-stage__canvas" ref={canvasRef}>
          <div class="media-stage__plane" style={planeStyle}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={videoStyle}
              aria-label={`Shared screen, rotated ${rotation} degrees`}
              onLoadedMetadata={(event) =>
                setNaturalSize({
                  width: event.currentTarget.videoWidth,
                  height: event.currentTarget.videoHeight,
                })
              }
            />
          </div>
        </div>
        {!hasVideo ? (
          <div class="media-placeholder" role="status">
            <span class="media-placeholder__pulse" aria-hidden="true" />
            <strong>Waiting for the shared screen</strong>
            <span>It will appear here as soon as the host is ready.</span>
          </div>
        ) : null}
        {playbackBlocked && hasVideo ? (
          <button
            class="playback-prompt"
            type="button"
            onClick={() => {
              void videoRef.current?.play().then(() => setPlaybackBlocked(false));
            }}
          >
            Play shared screen
          </button>
        ) : null}
      </div>

      <div class="viewer-toolbar" aria-label="Screen controls">
        <div class="segmented-control" aria-label="Screen size">
          <button type="button" aria-pressed={mode === 'fit'} onClick={() => setDisplayMode('fit')}>
            Fit
          </button>
          <button
            type="button"
            aria-pressed={mode === 'native'}
            onClick={() => setDisplayMode('native')}
          >
            Native
          </button>
        </div>

        <div class="zoom-control" aria-label="Zoom controls">
          <button type="button" aria-label="Zoom out" onClick={() => setSafeZoom(zoom - 0.25)}>
            −
          </button>
          <output aria-live="polite" aria-label="Zoom level">
            {Math.round(zoom * 100)}%
          </output>
          <button type="button" aria-label="Zoom in" onClick={() => setSafeZoom(zoom + 0.25)}>
            +
          </button>
        </div>

        <div class="rotation-control" aria-label="Screen rotation controls">
          <button
            type="button"
            aria-label="Rotate screen counterclockwise"
            title="Rotate left"
            onClick={() => setRotation((current) => rotateScreen(current, 'counterclockwise'))}
          >
            <RotateIcon direction="counterclockwise" />
          </button>
          <output aria-live="polite" aria-label="Screen rotation">
            {rotation}°
          </output>
          <button
            type="button"
            aria-label="Rotate screen clockwise"
            title="Rotate right"
            onClick={() => setRotation((current) => rotateScreen(current, 'clockwise'))}
          >
            <RotateIcon direction="clockwise" />
          </button>
        </div>

        <label class="volume-control">
          <VolumeIcon muted={volume === 0} />
          <span class="sr-only">Volume</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={volume}
            aria-label={`Volume ${Math.round(volume * 100)} percent`}
            onInput={(event) => setVolume(Number(event.currentTarget.value))}
          />
        </label>

        {fullscreenSupported ? (
          <button class="toolbar-button" type="button" onClick={() => void toggleFullscreen()}>
            <FullscreenIcon active={fullscreen} />
            <span>{fullscreen ? 'Exit full screen' : 'Full screen'}</span>
          </button>
        ) : null}
      </div>
    </div>
  );
}

function RotateIcon({ direction }: { readonly direction: 'clockwise' | 'counterclockwise' }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {direction === 'clockwise' ? (
        <>
          <path d="M20 7v5h-5" />
          <path d="M19 12a7 7 0 1 0-2 5" />
        </>
      ) : (
        <>
          <path d="M4 7v5h5" />
          <path d="M5 12a7 7 0 1 1 2 5" />
        </>
      )}
    </svg>
  );
}

function pointerDistance(points: Map<number, { x: number; y: number }>): number {
  const [first, second] = [...points.values()];
  if (!first || !second) return 1;
  return Math.max(1, Math.hypot(second.x - first.x, second.y - first.y));
}

function VolumeIcon({ muted }: { readonly muted: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 10v4h3l4 3V7l-4 3H5Z" />
      {muted ? <path d="m16 10 4 4m0-4-4 4" /> : <path d="M16 9a4 4 0 0 1 0 6" />}
    </svg>
  );
}

function FullscreenIcon({ active }: { readonly active: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {active ? (
        <path d="M9 4v5H4m16 0h-5V4m0 16v-5h5M4 15h5v5" />
      ) : (
        <path d="M4 9V4h5m6 0h5v5m0 6v5h-5M9 20H4v-5" />
      )}
    </svg>
  );
}
